import { defineContract } from "../../defineContract";
import type { AuditAction, AuditSource, AuditStatus } from "./catalog";

/**
 * Circunstância do request — igual para toda ação. Só informativo: nada aqui
 * é filtro (o que é filtro virou coluna).
 */
export interface AuditContext {
  /** Nome de quem agiu NAQUELE momento (o usuário pode ser renomeado/excluído). */
  userName?: string;
  ip?: string;
  userAgent?: string;
  route?: string;
  /** Empresa de quem agiu, quando difere da empresa do registro (super-admin). */
  actorCompanyId?: number;
  serverId?: string;
}

/**
 * `meta` gravado. `context` = circunstância; `data` = foto da ação no formato
 * da estrutura `schema` (ver AUDIT_STRUCTS). Nada aqui repete coluna.
 */
export interface AuditMeta {
  context: AuditContext;
  /** `<estrutura>@<versão>`; null = ação sem dados próprios. */
  schema: string | null;
  data: Record<string, unknown> | null;
}

/** Um registro como GET /audit-logs devolve (sem companyId: a rota já é da empresa). */
export interface AuditLogDTO {
  id: number;
  userId: number | null;
  tokenId: string | null;
  source: AuditSource;
  /** Ação feita pelo suporte (super-admin). */
  support: boolean;
  action: AuditAction;
  status: AuditStatus;
  entityId: string | null;
  meta: AuditMeta;
  /** ISO 8601. */
  createdAt: string;
}

/** Filtros de GET /audit-logs. Datas em `YYYY-MM-DD`, dia de America/Sao_Paulo, inclusivas. */
export interface AuditLogFilters {
  action?: AuditAction;
  status?: AuditStatus;
  source?: AuditSource;
  userId?: number;
  /** Entidade do catálogo (`auditEntities()`); com `entityId`, o histórico dela. */
  entity?: string;
  /** Exige `entity`: ids se repetem entre entidades. */
  entityId?: string;
  /** true = só ações feitas pelo suporte. */
  support?: boolean;
  /**
   * Campos `filterable` da estrutura da ação. Exige `action`. Texto = "contém"
   * (sem diferenciar maiúsculas); enum/número = igualdade.
   * Na query string: `data.<campo>=valor`.
   */
  data?: Record<string, string>;
  /**
   * Busca livre: "contém" no alvo (campo `title` de cada estrutura) e no nome
   * de quem agiu. Sem índice — vale só dentro do bloco do dia, com teto de
   * blocos por resposta (custo limitado por request).
   */
  q?: string;
  from?: string;
  to?: string;
  /** Só super-admin: consultar outra empresa. Ignorado para os demais. */
  companyId?: number;
}

/** Prefixo dos filtros de `data` na query string. */
export const AUDIT_DATA_FILTER_PREFIX = "data.";

/** Teto do valor de um filtro de `data` e da busca livre `q`. */
export const AUDIT_DATA_FILTER_MAX = 120;

export interface AuditLogParams extends Omit<AuditLogFilters, "data"> {
  /** Opaco — o `nextCursor` da resposta anterior, devolvido como veio. */
  cursor?: string;
  /** Filtros de `data`, achatados: `{ "data.name": "Suporte" }`. */
  [dataFilter: `data.${string}`]: string;
}

export interface AuditLogPage {
  records: AuditLogDTO[];
  /** null = acabou. */
  nextCursor: string | null;
  /**
   * Dia mais antigo (`YYYY-MM-DD`) que esta resposta percorreu. Com filtro
   * esparso a resposta pode vir vazia mas com `nextCursor` (bateu no teto de
   * blocos): é isto que deixa a tela dizer "nada até dd/mm — continuar?".
   */
  scannedUntil: string | null;
  /**
   * true = esta resposta nunca muda (só dias fechados, a partir de um cursor):
   * pode ficar em cache sem prazo curto. false = inclui o topo de um dia ainda
   * aberto, onde registros novos entram.
   */
  immutable: boolean;
}

/** Mínimo de registros por resposta (a última pode ter menos). */
export const AUDIT_LOG_PAGE_SIZE = 50;

export const auditLogsContract = defineContract<AuditLogParams, AuditLogPage>({
  route: "/audit-logs",
  method: "GET",
  // Sem invalidação por evento de empresa: o sistema inteiro grava auditoria o
  // tempo todo, e um emit por registro para cada atendente conectado custaria
  // mais do que economiza. O registro novo vai só para a sala de quem está na
  // tela (AUDIT_SOCKET), que mostra "N novos" e o botão "Atualizar"; o resto
  // vence por TTL curto, e o imutável fica pelo AUDIT_LOG_IMMUTABLE_TTL.
  cache: { ttl: 30 * 1000, invalidatedBy: [] }
});

/**
 * Sala de quem está com a tela de auditoria aberta. Só admin/super entra (o
 * backend confere no JOIN) e ela é RESERVADA: os joins genéricos
 * (joinTickets/joinChatBox) recusam o nome — ver isReservedRoom.
 *
 * Emitir para a sala vazia não custa nada: é assim que o evento por registro
 * não pesa para quem não está olhando.
 */
export const AUDIT_SOCKET = {
  ROOM: "audit-logs",
  JOIN: "joinAuditLogs",
  LEAVE: "leaveAuditLogs"
} as const;

/** Payload de SOCKET_EVENTS.AUDIT.RECORDED. */
export interface AuditRecordedSocketPayload {
  record: AuditLogDTO;
}

/** Quanto o front guarda uma resposta `immutable` (ela não muda; o teto é memória). */
export const AUDIT_LOG_IMMUTABLE_TTL = 60 * 60 * 1000;
